import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiFlag, FiAlertCircle } from "react-icons/fi";

const ReportModal = ({ isOpen, onClose, onSubmit, messageId }) => {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { value: "spam", label: "Spam", icon: "📧" },
    { value: "harassment", label: "Harassment", icon: "😡" },
    { value: "inappropriate", label: "Inappropriate Content", icon: "⚠️" },
    { value: "abuse", label: "Abuse or Threats", icon: "🚫" },
    { value: "other", label: "Other", icon: "📝" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      return;
    }

    setSubmitting(true);

    try {
      await onSubmit(messageId, { reason, description });
      setReason("");
      setDescription("");
      onClose();
    } catch (error) { /* ignored */ } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setDescription("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="msgx-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="msgx-modal msgx-modal-report"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="msgx-modal-header">
              <div className="msgx-modal-title-row">
                <FiFlag className="msgx-modal-title-icon" />
                <h2 className="msgx-modal-title">Report Message</h2>
              </div>
              <button className="msgx-modal-close" onClick={handleClose}>
                <FiX />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="msgx-modal-form">
              {/* Reason Selection */}
              <div className="msgx-form-group">
                <label className="msgx-form-label">
                  <FiAlertCircle />
                  Reason for report
                </label>
                <div className="msgx-report-reasons">
                  {reasons.map((r) => (
                    <motion.button
                      key={r.value}
                      type="button"
                      className={`msgx-report-reason ${
                        reason === r.value ? "selected" : ""
                      }`}
                      onClick={() => setReason(r.value)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="msgx-report-reason-icon">{r.icon}</span>
                      <span className="msgx-report-reason-label">
                        {r.label}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="msgx-form-group">
                <label className="msgx-form-label" htmlFor="description">
                  Additional Details (Optional)
                </label>
                <textarea
                  id="description"
                  className="msgx-form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide more context about why you're reporting this message..."
                  rows={4}
                  maxLength={500}
                />
                <span className="msgx-form-char-count">
                  {description.length}/500
                </span>
              </div>

              {/* Actions */}
              <div className="msgx-modal-actions">
                <motion.button
                  type="button"
                  className="msgx-btn msgx-btn-secondary"
                  onClick={handleClose}
                  disabled={submitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  className="msgx-btn msgx-btn-danger"
                  disabled={!reason || submitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitting ? (
                    <>
                      <div className="msgx-btn-spinner" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FiFlag />
                      Submit Report
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
